import { PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, message, Modal, Row, Spin } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { Trans, useTranslation } from 'react-i18next';
import { formatDisplayDate, ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import StationInput from '@/components/FormItems/StationInput';
import AppDatePicker from '@/components/FormItems/DatePicker';
import moment from 'moment';
import { CompanyTicketClassDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

const columns: IColumnType<CompanyTicketClassDto>[] = [
  {
    title: 'busStationMappingsPage_Bus',
    dataIndex: 'busName',
    defaultSortOrder: 'ascend',
  },
  {
    title: 'busStationMappingsPage_Station',
    dataIndex: 'stationName',
  },
  {
    title: 'busStationMappingsPage_StartDate',
    dataIndex: 'startDate',
    render: v => formatDisplayDate(v),
  },
  {
    title: 'busStationMappingsPage_EndDate',
    dataIndex: 'endDate',
    render: v => formatDisplayDate(v),
  },
];

const filters: Array<IFilterItem> = [
  {
    type: 'SELECT',
    name: 'stationId',
    label: 'busStationMappingsPage_Station',
    customElm: <StationInput placeholder={<Trans>busStationMappingsPage_Station</Trans>} />,
    mdSize: 10,
    lgSize: 10,
    xlSize: 10,
    xxlSize: 10,
  },
];
type Props = {
  onClose: () => void;
  busId: string;
};

const BusStationMappingsModal = ({ onClose, busId }: Props) => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const createEntity = values => {
    setLoading(true);
    apiSdk.BusStationMappingService.create({ body: { ...values, busId: busId } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.BusStationMappingService.update({ body: { ...values, busId: busId } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      visible={true}
      maskClosable={false}
      width={ModalWidth.medium}
      className="nta-custom-form"
      title={t('busStationMappingsPage_Title')}
      forceRender={true}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={() => {
        onClose();
      }}
    >
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        defaultFilter={{ busId: busId }}
        url="/fleets/bus-station-mappings/get-bus-station-mappings"
        bordered
        order_by="busId"
        order_by_asc={true}
        allowEdit={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
          });
        }}
        allowDelete={true}
        delete_url="/fleets/bus-station-mappings/delete"
        filters={filters}
        searchBtnAlignLeft
        extraBtns={[
          <Button
            key="create"
            type="primary"
            className="nta-create-btn"
            icon={<PlusCircleOutlined />}
            onClick={() => {
              form.resetFields();
              setIsNewForm(true);
              setFormVisible(true);
            }}
          >
            {t('createNew')}
          </Button>,
        ]}
      ></ZTable>
      <Modal
        visible={formVisible}
        maskClosable={false}
        width={ModalWidth.small}
        className="nta-custom-form"
        title={isNewForm ? t('busStationMappingsPage_CreateBusStationMapping') : t('busStationMappingsPage_UpdateBusStationMapping')}
        forceRender={true}
        okText={t('save')}
        cancelText={t('cancel')}
        okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
        onCancel={() => {
          form.resetFields();
          setFormVisible(false);
        }}
        onOk={() => {
          form
            .validateFields()
            .then(values => {
              if (values.id) {
                updateEntity(values);
              } else {
                createEntity(values);
              }
            })
            .catch(info => {});
        }}
      >
        <Spin spinning={loading}>
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>

            <Row gutter={ROW_GUTTER}>
              <Col md={24}>
                <Form.Item label={t('busStationMappingsPage_Station')} name="stationId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <StationInput />
                </Form.Item>
                <Form.Item noStyle dependencies={['endDate']}>
                  {({ getFieldValue }) => {
                    const endDate = getFieldValue('endDate');
                    return (
                      <Form.Item
                        label={t('busStationMappingsPage_StartDate')}
                        name="startDate"
                        rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                      >
                        <AppDatePicker
                          className="w-100"
                          disabledDate={d => {
                            if (endDate) {
                              return d.endOf('day') > moment(endDate).endOf('day');
                            }
                            return false;
                          }}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
                <Form.Item noStyle dependencies={['startDate']}>
                  {({ getFieldValue }) => {
                    const startDate = getFieldValue('startDate');
                    return (
                      <Form.Item label={t('busStationMappingsPage_EndDate')} name="endDate" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <AppDatePicker
                          className="w-100"
                          disabledDate={d => {
                            if (startDate) {
                              return d.endOf('day') < moment(startDate).endOf('day');
                            }
                            return false;
                          }}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
    </Modal>
  );
};
export default BusStationMappingsModal;
